import { useState } from "react";
import { useMutation } from "@apollo/client";
import { Loader2, ArrowRight, Sparkles } from "@credcore/core-ui/icons";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn-components/Card";
import { Button } from "@/components/shadcn-components/Button";
import { Input } from "@/components/shadcn-components/Input";
import { Label } from "@/components/shadcn-components/Label";
import { Switch } from "@/components/shadcn-components/Switch";
import { RUN_CLASSIFICATION } from "@/graphql/queries";

interface ClassificationFormState {
  batchSize: string;
  parallel: string;
  continueOnError: boolean;
  dryRun: boolean;
}

interface ClassificationTypeCount {
  type: string;
  count: number;
}

interface ClassificationResultData {
  success: boolean;
  total: number;
  classified: number;
  failed: number;
  chunks?: number;
  byType: ClassificationTypeCount[];
  dryRun: boolean;
  errorMessage?: string;
}

const INITIAL_FORM_STATE: ClassificationFormState = {
  batchSize: "100",
  parallel: "1",
  continueOnError: false,
  dryRun: false,
};

export function ClassificationForm() {
  const [formState, setFormState] =
    useState<ClassificationFormState>(INITIAL_FORM_STATE);
  const [result, setResult] = useState<ClassificationResultData | null>(null);

  const [runClassification, { loading: isRunning }] = useMutation(
    RUN_CLASSIFICATION,
    {
      onCompleted: (data) => {
        setResult(data.runClassification);
      },
      onError: (error) => {
        setResult({
          success: false,
          total: 0,
          classified: 0,
          failed: 0,
          byType: [],
          dryRun: formState.dryRun,
          errorMessage: error.message,
        });
      },
    },
  );

  const handleInputChange = (
    field: keyof ClassificationFormState,
    value: string | boolean,
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const input = {
      dryRun: formState.dryRun,
      batchSize: parseInt(formState.batchSize) || 100,
      parallel: parseInt(formState.parallel) || 1,
      continueOnError: formState.continueOnError,
    };

    runClassification({ variables: { input } });
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          AI Classification
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <div className="flex-1 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="batchSize">Batch Size</Label>
              <Input
                id="batchSize"
                type="number"
                value={formState.batchSize}
                onChange={(e) => handleInputChange("batchSize", e.target.value)}
                placeholder="100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parallel">Parallel Workers</Label>
              <Input
                id="parallel"
                type="number"
                value={formState.parallel}
                onChange={(e) => handleInputChange("parallel", e.target.value)}
                placeholder="1"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="continueOnError"
                checked={formState.continueOnError}
                onCheckedChange={(checked) =>
                  handleInputChange("continueOnError", checked)
                }
              />
              <Label htmlFor="continueOnError">Continue on Error</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="classifyDryRun"
                checked={formState.dryRun}
                onCheckedChange={(checked) =>
                  handleInputChange("dryRun", checked)
                }
              />
              <Label htmlFor="classifyDryRun">Dry Run</Label>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <Button
            onClick={handleSubmit}
            disabled={isRunning}
            className="w-full"
          >
            {isRunning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Classifying...
              </>
            ) : (
              <>
                <ArrowRight className="mr-2 h-4 w-4" />
                Run Classification
              </>
            )}
          </Button>

          {result && (
            <div
              className={`rounded-md p-4 ${
                result.success
                  ? "bg-green-50 text-green-800"
                  : "bg-red-50 text-red-800"
              }`}
            >
              <h4 className="font-medium">
                {result.dryRun ? "Dry Run Result" : "Classification Result"}
              </h4>
              <dl className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt>Total Processed:</dt>
                  <dd className="font-medium">{result.total}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Classified:</dt>
                  <dd className="font-medium text-green-600">
                    {result.classified}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Failed:</dt>
                  <dd className="font-medium text-red-600">{result.failed}</dd>
                </div>
                {result.chunks && (
                  <div className="flex justify-between">
                    <dt>Batches:</dt>
                    <dd>{result.chunks}</dd>
                  </div>
                )}
                {result.byType.length > 0 && (
                  <div className="mt-3 border-t border-gray-200 pt-2">
                    <dt className="font-medium">By Type:</dt>
                    <dd className="mt-1 space-y-1">
                      {result.byType.map((item) => (
                        <div
                          key={item.type}
                          className="flex justify-between text-xs"
                        >
                          <span>{item.type.replace(/_/g, " ")}</span>
                          <span className="font-medium">{item.count}</span>
                        </div>
                      ))}
                    </dd>
                  </div>
                )}
                {result.errorMessage && (
                  <div className="mt-2 text-red-700">
                    <dt className="font-medium">Error:</dt>
                    <dd>{result.errorMessage}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
